/**
 * PillarCard (DC-1492)
 *
 * Card-per-pillar visualization of one TriangulationCell. Anuj locked this
 * shape on 2026-05-14 over the matrix and heat-map alternatives — four big
 * cards (F / A / I / R), each showing the three leg readings + coherence
 * + named pattern.
 *
 * Editorial redesign (2026-05-26): re-laid-out onto the cream/teal system so
 * the card leads with the diagnosis, not the raw numbers.
 *
 * Composition, top to bottom:
 *   - Header: pillar letter + full name + state descriptor
 *   - PatternBadge — the diagnosis, given headline weight
 *   - Three LegReadingRows (Technical / Perceived / Governed) as a
 *     shared-scale convergence read
 *   - Coherence bar
 *   - "What to do" — the action layer (Section 6 of the user guide): what the
 *     pattern means and the first move, OR — when a pattern is suspended —
 *     exactly what unlocks it. All derived from the real cell; nothing invented.
 */

import React from 'react';
import type { TriangulationCell, FairPillarName, PatternName } from '../../types/triangulation';
import { LegReadingRow } from './LegReadingRow';
import { CoherenceBar } from './CoherenceBar';
import { PatternBadge } from './PatternBadge';
import { RoleGapStrip } from './RoleGapStrip';
import { confidenceLabel, legBreached, preliminaryLegDetails } from './legUtils';

interface PillarCardProps {
  cell: TriangulationCell;
  /** v2.5 SS-03 (G0 §6): declared-absence attribution + methodology, passed
   *  down from the domain view for the visible confidence disclosure. */
  declaredByName?: string | null;
  declaredAt?: string | null;
  methodologyVersion?: string | null;
}

const PILLAR_LETTERS: Record<FairPillarName, 'F' | 'A' | 'I' | 'R'> = {
  Findable: 'F',
  Accessible: 'A',
  Interoperable: 'I',
  Reusable: 'R',
};

/**
 * Per-pattern guidance — the one-line "what it means" + "first move" from the
 * Triangulated FAIR Assessment user guide (Section 6). These are the fixed
 * meanings of the eight canonical patterns, not per-domain inference, so this
 * static lookup is the same defensible vocabulary the guide publishes.
 */
const PATTERN_GUIDANCE: Record<PatternName, { meaning: string; move: string }> = {
  'Confirmed Maturity': {
    meaning: 'All three signals agree this domain is in good shape.',
    move: 'Preserve and propagate — package it as a template another domain can adopt.',
  },
  'Aligned Maturity': {
    meaning: 'Technical and Perceived agree on high maturity; the documented signal is the tiebreaker.',
    move: 'Review the governance documents to confirm or contest it.',
  },
  Greenfield: {
    meaning: 'All three legs agree the domain is early-stage — no hidden contradiction.',
    move: 'Decide whether to invest here; this is a roadmap call, not remediation.',
  },
  'Usability Gap': {
    meaning: 'Metadata and governance are strong, but the people who use the data can’t.',
    move: 'Examine discoverability and training — a Usability Gap rarely resolves by writing more SOPs.',
  },
  'Tribal Knowledge': {
    meaning: 'Stakeholders rate it high, but the maturity lives in two or three heads.',
    move: 'Capture the practice in an SOP before the knowledge walks out.',
  },
  'Unformalized Practice': {
    meaning: 'The data is solid and the team works well — but no SOP describes how.',
    move: 'Write the SOP that codifies what already happens.',
  },
  'Abandoned Capability': {
    meaning: 'Solid metadata, nobody uses the data, and no SOP governs it.',
    move: 'Decide: re-invest with a clear use case, or formally retire it.',
  },
  'Policy Theater': {
    meaning: 'The documentation describes a system the data and the people don’t reflect.',
    move: 'Close the document-vs-reality gap — the gap itself is the audit risk.',
  },
};

interface LegBreach {
  technical: boolean;
  perceived: boolean;
}

/**
 * The hard-floor breach derivation is SHARED with the SynthesisBand via
 * legUtils (v2.5 SS-03 — the twin copies previously drifted). Keep legUtils
 * aligned with the server `patternIdentifier`'s gating logic.
 */
function legBreaches(cell: TriangulationCell): LegBreach {
  return {
    technical: legBreached(cell, 'technical'),
    perceived: legBreached(cell, 'perceived'),
  };
}

interface CardGuidance {
  meaning: string;
  /** Plain next-step text. */
  move?: string;
  /** When set, the next step is an unlock affordance (collect more signal). */
  unlock?: string;
}

/**
 * Resolve the card's guidance from the REAL cell state — never invents a
 * pattern the engine didn't emit. When the pattern is suspended, it explains
 * what is blocking it and what unlocks it.
 */
function resolveGuidance(cell: TriangulationCell, breach: LegBreach): CardGuidance {
  if (cell.pattern !== null) {
    // v2.5 SS-03 round 1: guidance is EVIDENCE-BASIS-AWARE. With Documented
    // deliberately declared absent, the static copy would lie ("all three
    // legs agree") or send users to review documents that were explicitly
    // declared not to exist.
    if (cell.evidence_basis === 'documented_declared_absent') {
      const declaredAbsent: Partial<Record<PatternName, CardGuidance>> = {
        Greenfield: {
          meaning:
            'The two available legs agree the domain is early-stage; governance documentation was deliberately declared absent — a recorded decision, not a gap.',
          move: 'Decide whether to invest here; this is a roadmap call, not remediation.',
        },
        'Aligned Maturity': {
          meaning:
            'Technical and Perceived agree on high maturity; the Documented leg was deliberately declared absent, so no document review is pending.',
          move:
            'Treat the declared absence as a standing decision — revisit it only if governance artifacts are created for this domain.',
        },
      };
      const override = declaredAbsent[cell.pattern];
      if (override) return override;
      const g = PATTERN_GUIDANCE[cell.pattern];
      return {
        meaning: `${g.meaning} (Documented was deliberately declared absent — the pattern rests on the two available legs.)`,
        move: g.move,
      };
    }
    const g = PATTERN_GUIDANCE[cell.pattern];
    return { meaning: g.meaning, move: g.move };
  }
  // pattern === null
  const hardFloorBreached = breach.technical || breach.perceived;
  if (!hardFloorBreached) {
    return {
      meaning: 'A leg sits in the mid-bucket (2.5–3.5); naming a pattern would over-claim.',
      move: 'Drill into the underlying rule, interview, and document evidence to see why.',
    };
  }

  // Hard-floor breach → name what's missing, observe what the gathered legs
  // already show (a factual description of the real scores — never a pattern
  // the engine didn't emit), and tell the user exactly what unlocks the read.
  const missing: string[] = [];
  if (breach.technical) missing.push('the Technical leg hasn’t been gathered');
  if (breach.perceived) missing.push('the Perceived leg is below its floor');
  const meaning = `Pattern suspended — ${missing.join(' and ')}.${observeGatheredLegs(cell)}`;

  const unlocks: string[] = [];
  if (breach.technical) unlocks.push('run FAIR scoring on this domain’s datasets (≥5 to clear the floor)');
  if (breach.perceived) unlocks.push('interview ≥3 persona archetypes');
  return { meaning, unlock: unlocks.join(', and ') };
}

const LEG_NAME = { technical: 'Technical', perceived: 'Perceived', documented: 'Governed' } as const;

/**
 * A factual one-clause read of the legs that ARE gathered — describes the real
 * scores (and whether they agree or diverge) without asserting any engine
 * pattern. Returns '' unless exactly two legs are scored.
 */
function observeGatheredLegs(cell: TriangulationCell): string {
  const legs = (['technical', 'perceived', 'documented'] as const).filter(
    (l) => cell[l].state === 'present_and_scored' && cell[l].score !== null,
  );
  if (legs.length !== 2) return '';
  const [a, b] = legs;
  const sa = cell[a].score as number;
  const sb = cell[b].score as number;
  if (Math.abs(sa - sb) >= 1) {
    const [hi, lo] = sa >= sb ? [a, b] : [b, a];
    return ` Meanwhile ${LEG_NAME[hi]} (${(cell[hi].score as number).toFixed(1)}) and ${LEG_NAME[lo]} (${(cell[lo].score as number).toFixed(1)}) already diverge — the missing leg will show which way it resolves.`;
  }
  return ` The two gathered legs agree closely (${sa.toFixed(1)} and ${sb.toFixed(1)}).`;
}

const STATE_DESCRIPTOR = (cell: TriangulationCell, breach: LegBreach): string => {
  if (cell.pattern !== null) {
    // v2.5 SS-03: BOTH confidence facts compose into the descriptor —
    // "Suspected · Perceived coverage preliminary", never one word hiding
    // the other dimension.
    return confidenceLabel(cell) ?? 'Confirmed';
  }
  return breach.technical || breach.perceived ? 'Awaiting evidence' : 'Under review';
};

export const PillarCard: React.FC<PillarCardProps> = ({
  cell,
  declaredByName = null,
  declaredAt = null,
  methodologyVersion = null,
}) => {
  const letter = PILLAR_LETTERS[cell.pillar];
  const breach = legBreaches(cell);
  const hardFloorBreached = breach.technical || breach.perceived;
  const guidance = resolveGuidance(cell, breach);

  return (
    <section
      data-testid="pillar-card"
      data-pillar={cell.pillar}
      aria-label={`${cell.pillar} pillar triangulation`}
      className="tri-card"
    >
      <header className="tri-card-head">
        <span className="tri-pmark" aria-hidden="true">
          {letter}
        </span>
        <h3 className="tri-pname">
          {cell.pillar}
          <small>{STATE_DESCRIPTOR(cell, breach)}</small>
        </h3>
      </header>

      <PatternBadge
        pattern={cell.pattern}
        qualifier={cell.pattern_qualifier}
        evidenceBasis={cell.evidence_basis}
        coverageConfidence={cell.coverage_confidence}
        preliminaryLegDetails={preliminaryLegDetails(cell)}
        methodologyVersion={methodologyVersion}
        declaredByName={declaredByName}
        declaredAt={declaredAt}
        hardFloorBreached={hardFloorBreached}
      />

      <div className="tri-legs">
        <LegReadingRow shortLabel="T" fullLabel="Technical" reading={cell.technical} />
        <LegReadingRow shortLabel="P" fullLabel="Perceived" reading={cell.perceived} />
        {/* DC-1612: the gap rides the Perceived territory — a refinement of
            that leg, not a fourth row. Absent unless the fetch requested the
            role breakdown AND something attributed to a role. */}
        <RoleGapStrip reading={cell.rolePerception} />
        <LegReadingRow shortLabel="G" fullLabel="Governed" reading={cell.documented} />
      </div>

      <div className="tri-card-foot">
        <CoherenceBar coherence={cell.coherence} />
        <div className="tri-do">
          <b>What to do:</b> {guidance.meaning}{' '}
          {guidance.move ? (
            guidance.move
          ) : (
            <span className="tri-unlock" role="note">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
              {guidance.unlock}
            </span>
          )}
        </div>
      </div>
    </section>
  );
};
